/* eslint-disable react/prop-types */
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Button, Card, Col, Grid, Input, List, Row, Spin, Tag, Transfer, Typography,
} from 'antd';
import {
  LoadingOutlined, SaveOutlined, SearchOutlined, UndoOutlined,
} from '@ant-design/icons';
import { getRolesDeUsuario } from 'api/estructuraOrganizacional/rolesDeUsuario';
import { getUsuariosDeRol, getUsuariosParaRoles, patchUsuariosDeRol } from 'api/usuarios/rolesUsuarios';
import { checkToken } from 'api/utils';
import ModalConfirmation from 'components/ModalConfirmation';

const { Text } = Typography;
const { useBreakpoint } = Grid;

export const rolesYUsuariosPermission = {
  permissionModel: 'rol',
  catalogsModels: [],
  tabsModels: [],
};

const sameKeys = (a, b) => a.length === b.length && a.every((k) => b.includes(k));

const RolesYUsuarios = ({ permission }) => {
  const screens = useBreakpoint();
  const [roles, setRoles] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [rolSeleccionado, setRolSeleccionado] = useState(null);
  const [guardados, setGuardados] = useState([]);
  const [targetKeys, setTargetKeys] = useState([]);
  const [busquedaRol, setBusquedaRol] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingRol, setLoadingRol] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rolPendiente, setRolPendiente] = useState(null);

  const puedeEditar = !!permission?.change;
  const hayCambios = rolSeleccionado !== null && !sameKeys(guardados, targetKeys);
  // Usuarios agregados + quitados respecto a lo ya guardado
  const cambiosPendientes = targetKeys.filter((k) => !guardados.includes(k)).length
    + guardados.filter((k) => !targetKeys.includes(k)).length;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await checkToken();
      const [_roles, _usuarios] = await Promise.all([
        getRolesDeUsuario(),
        getUsuariosParaRoles(),
      ]);
      setRoles(_roles);
      setUsuarios(_usuarios);
      setLoading(false);
    };
    fetchData();
  }, []);

  const dataSource = useMemo(() => usuarios.map((u) => ({
    key: u.id,
    title: u.nombre || u.email,
    description: u.email,
  })), [usuarios]);

  const rolesFiltrados = useMemo(() => {
    const q = busquedaRol.trim().toLowerCase();
    return q ? roles.filter((r) => r.name?.toLowerCase().includes(q)) : roles;
  }, [roles, busquedaRol]);

  const cargarRol = useCallback(async (rol) => {
    setLoadingRol(true);
    await checkToken();
    const ids = await getUsuariosDeRol(rol.id);
    if (ids) {
      setRolSeleccionado(rol);
      setGuardados(ids);
      setTargetKeys(ids);
    }
    setLoadingRol(false);
  }, []);

  const onSeleccionarRol = (rol) => {
    if (rol.id === rolSeleccionado?.id) return;
    if (hayCambios) {
      setRolPendiente(rol);
      return;
    }
    cargarRol(rol);
  };

  const onGuardar = async () => {
    setSaving(true);
    await checkToken();
    const ids = await patchUsuariosDeRol(rolSeleccionado.id, targetKeys);
    if (ids) {
      setGuardados(ids);
      setTargetKeys(ids);
    }
    setSaving(false);
  };

  return (
    <Spin spinning={loading}>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={7}>
          <Card title="Roles" size="small">
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Buscar Rol"
              value={busquedaRol}
              onChange={(e) => setBusquedaRol(e.target.value)}
              style={{ marginBottom: 8 }}
            />
            <List
              size="small"
              dataSource={rolesFiltrados}
              style={{ maxHeight: 520, overflowY: 'auto' }}
              locale={{ emptyText: 'Sin roles' }}
              renderItem={(rol) => {
                const activo = rol.id === rolSeleccionado?.id;
                return (
                  <List.Item
                    onClick={() => onSeleccionarRol(rol)}
                    style={{
                      cursor: 'pointer',
                      background: activo ? '#e6f7ff' : undefined,
                      fontWeight: activo ? 600 : undefined,
                    }}
                  >
                    {rol.name}
                  </List.Item>
                );
              }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={17}>
          <Card
            size="small"
            title={rolSeleccionado ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8, minWidth: 0,
              }}
              >
                <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>Usuarios del Rol:</Text>
                <Text
                  strong
                  ellipsis={{ tooltip: rolSeleccionado.name }}
                  style={{ flex: 1, minWidth: 0 }}
                >
                  {rolSeleccionado.name}
                </Text>
                <Tag color={hayCambios ? 'orange' : 'blue'} style={{ margin: 0 }}>
                  {`${targetKeys.length} usuarios${hayCambios ? ` · ${cambiosPendientes} Sin Guardar` : ''}`}
                </Tag>
              </div>
            ) : 'Usuarios'}
            extra={rolSeleccionado && (
              <Row className="form-submit-controls" wrap={false}>
                <Button
                  type="link"
                  onClick={() => setTargetKeys(guardados)}
                  disabled={!hayCambios || saving}
                >
                  <UndoOutlined />
                  {screens.md && 'Descartar'}
                </Button>
                <Button
                  type="link"
                  onClick={onGuardar}
                  disabled={!hayCambios || !puedeEditar || saving}
                >
                  {saving ? <LoadingOutlined /> : <SaveOutlined />}
                  {screens.md && 'Guardar'}
                </Button>
              </Row>
            )}
          >
            <Spin spinning={loadingRol}>
              {!rolSeleccionado ? (
                <Text type="secondary">
                  Seleccione un rol de la lista para ver y asignar sus usuarios.
                </Text>
              ) : (
                <Transfer
                  showSearch
                  dataSource={dataSource}
                  targetKeys={targetKeys}
                  onChange={(keys) => setTargetKeys(keys)}
                  disabled={!puedeEditar || saving}
                  titles={['Usuarios sin el Rol', 'Usuarios con el Rol']}
                  style={{ width: '100%' }}
                  listStyle={{ width: 'calc(50% - 20px)', minWidth: 0, height: 460 }}
                  filterOption={(input, item) => `${item.title} ${item.description}`
                    .toLowerCase()
                    .includes(input.trim().toLowerCase())}
                  render={(item) => (
                    <div
                      title={`${item.title} - ${item.description}`}
                      style={{
                        display: 'flex', flexDirection: 'column', lineHeight: 1.3, minWidth: 0,
                      }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.title}
                      </span>
                      <Text
                        type="secondary"
                        style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {item.description}
                      </Text>
                    </div>
                  )}
                  locale={{
                    itemUnit: 'usuario',
                    itemsUnit: 'usuarios',
                    searchPlaceholder: 'Buscar Usuario',
                    notFoundContent: 'Sin usuarios',
                  }}
                />
              )}
            </Spin>
          </Card>
        </Col>
      </Row>
      <ModalConfirmation
        onFinish={() => {
          const rol = rolPendiente;
          setRolPendiente(null);
          cargarRol(rol);
        }}
        onFinishText="Descartar y Cambiar"
        onCancel={() => setRolPendiente(null)}
        open={rolPendiente !== null}
        contentTitle={`Descartar los cambios sin guardar y cambiar al rol ${rolPendiente?.name || ''}`}
      />
    </Spin>
  );
};

export default RolesYUsuarios;
